import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import {
  IReconcileResponse,
  IRebuildResponse,
  ICalendarValidationResponse,
  IHistoricDetectionResponse,
  IBackfillRunbookResponse,
} from "./GLSummaryReconcile.types";
import { IStoreState } from "../../initialStoreState";
import { showMessage } from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { translateBackendError } from "../../../helpers/errorTranslator";

// ============================================================================
// Reconcile (GET) — the only thunk that persists its response into the reducer
// ============================================================================
export const FETCH_RECONCILE_PROGRESS = "FETCH_RECONCILE_PROGRESS";
export const FETCH_RECONCILE_SUCCESS = "FETCH_RECONCILE_SUCCESS";
export const FETCH_RECONCILE_FAILED = "FETCH_RECONCILE_FAILED";

export const fetchReconcileProgress = () => action(FETCH_RECONCILE_PROGRESS);
export const fetchReconcileSuccess = (data: IReconcileResponse) =>
  action(FETCH_RECONCILE_SUCCESS, { data });
export const fetchReconcileFailed = () => action(FETCH_RECONCILE_FAILED);

interface IFetchReconcileParams {
  fiscal_year_month_code?: string;
}

export const fetchReconcileAsync =
  ({
    fiscal_year_month_code,
  }: IFetchReconcileParams = {}): ThunkAction<
    void,
    IStoreState,
    {},
    AnyAction
  > =>
  async (dispatch) => {
    try {
      dispatch(fetchReconcileProgress());

      let finalUrl = "/accounting/gl-monthly-summary/reconcile";
      if (fiscal_year_month_code) {
        finalUrl += `?fiscal_year_month_code=${fiscal_year_month_code}`;
      }

      const res = await api.get(finalUrl);
      const data: IReconcileResponse = res.data.data;
      dispatch(fetchReconcileSuccess(data));
    } catch (err: any) {
      dispatch(fetchReconcileFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

// ============================================================================
// Rebuild snapshot (POST)
// ============================================================================
export const REBUILD_SNAPSHOT_PROGRESS = "REBUILD_SNAPSHOT_PROGRESS";
export const REBUILD_SNAPSHOT_SUCCESS = "REBUILD_SNAPSHOT_SUCCESS";
export const REBUILD_SNAPSHOT_FAILED = "REBUILD_SNAPSHOT_FAILED";

export const rebuildSnapshotProgress = () => action(REBUILD_SNAPSHOT_PROGRESS);
export const rebuildSnapshotSuccess = () => action(REBUILD_SNAPSHOT_SUCCESS);
export const rebuildSnapshotFailed = () => action(REBUILD_SNAPSHOT_FAILED);

interface IRebuildSnapshotParams {
  fiscal_year_month_code: string;
  cascade: boolean;
}

export const rebuildSnapshotAsync =
  (
    { fiscal_year_month_code, cascade }: IRebuildSnapshotParams,
    onCallback?: (isSuccess: boolean, response?: IRebuildResponse) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(rebuildSnapshotProgress());

      const res = await api.post("/accounting/gl-monthly-summary/rebuild", {
        fiscal_year_month_code,
        cascade,
      });
      const response: IRebuildResponse = res.data.data;
      dispatch(rebuildSnapshotSuccess());
      dispatch(
        showMessage({
          type: "success",
          message: "Snapshot rebuild completed!",
          displayAs: "snackbar",
        }),
      );

      if (onCallback) {
        onCallback(true, response);
      }
    } catch (err: any) {
      dispatch(rebuildSnapshotFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
      if (onCallback) {
        onCallback(false);
      }
    }
  };

// ============================================================================
// Nightly reconcile (POST)
// ============================================================================
export const RUN_NIGHTLY_RECONCILE_PROGRESS =
  "RUN_NIGHTLY_RECONCILE_PROGRESS";
export const RUN_NIGHTLY_RECONCILE_SUCCESS = "RUN_NIGHTLY_RECONCILE_SUCCESS";
export const RUN_NIGHTLY_RECONCILE_FAILED = "RUN_NIGHTLY_RECONCILE_FAILED";

export const runNightlyReconcileProgress = () =>
  action(RUN_NIGHTLY_RECONCILE_PROGRESS);
export const runNightlyReconcileSuccess = () =>
  action(RUN_NIGHTLY_RECONCILE_SUCCESS);
export const runNightlyReconcileFailed = () =>
  action(RUN_NIGHTLY_RECONCILE_FAILED);

export const runNightlyReconcileAsync =
  (
    onCallback?: (isSuccess: boolean, response?: IReconcileResponse) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(runNightlyReconcileProgress());

      const res = await api.post(
        "/accounting/gl-monthly-summary/nightly-reconcile",
      );
      const response: IReconcileResponse = res.data.data;
      dispatch(runNightlyReconcileSuccess());
      dispatch(
        showMessage({
          type: "success",
          message: "Nightly reconcile completed!",
          displayAs: "snackbar",
        }),
      );

      if (onCallback) {
        onCallback(true, response);
      }
    } catch (err: any) {
      dispatch(runNightlyReconcileFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
      if (onCallback) {
        onCallback(false);
      }
    }
  };

// ============================================================================
// Validate fiscal calendar (GET)
// ============================================================================
export const VALIDATE_FISCAL_CALENDAR_PROGRESS =
  "VALIDATE_FISCAL_CALENDAR_PROGRESS";
export const VALIDATE_FISCAL_CALENDAR_SUCCESS =
  "VALIDATE_FISCAL_CALENDAR_SUCCESS";
export const VALIDATE_FISCAL_CALENDAR_FAILED =
  "VALIDATE_FISCAL_CALENDAR_FAILED";

export const validateFiscalCalendarProgress = () =>
  action(VALIDATE_FISCAL_CALENDAR_PROGRESS);
export const validateFiscalCalendarSuccess = () =>
  action(VALIDATE_FISCAL_CALENDAR_SUCCESS);
export const validateFiscalCalendarFailed = () =>
  action(VALIDATE_FISCAL_CALENDAR_FAILED);

export const validateFiscalCalendarAsync =
  (
    onCallback?: (
      isSuccess: boolean,
      response?: ICalendarValidationResponse,
    ) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(validateFiscalCalendarProgress());

      const res = await api.get("/accounting/fiscal-calendar/validate");
      const response: ICalendarValidationResponse = res.data.data;
      dispatch(validateFiscalCalendarSuccess());

      if (onCallback) {
        onCallback(true, response);
      }
    } catch (err: any) {
      dispatch(validateFiscalCalendarFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
      if (onCallback) {
        onCallback(false);
      }
    }
  };

// ============================================================================
// Detect historic RE entries (POST)
// ============================================================================
export const DETECT_HISTORIC_PROGRESS = "DETECT_HISTORIC_PROGRESS";
export const DETECT_HISTORIC_SUCCESS = "DETECT_HISTORIC_SUCCESS";
export const DETECT_HISTORIC_FAILED = "DETECT_HISTORIC_FAILED";

export const detectHistoricProgress = () => action(DETECT_HISTORIC_PROGRESS);
export const detectHistoricSuccess = () => action(DETECT_HISTORIC_SUCCESS);
export const detectHistoricFailed = () => action(DETECT_HISTORIC_FAILED);

interface IDetectHistoricParams {
  dry_run: boolean;
  tolerance_pct?: number;
  tolerance_min?: number;
}

export const detectHistoricAsync =
  (
    { dry_run, tolerance_pct, tolerance_min }: IDetectHistoricParams,
    onCallback?: (
      isSuccess: boolean,
      response?: IHistoricDetectionResponse,
    ) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(detectHistoricProgress());

      const res = await api.post(
        "/accounting/gl-fiscal-year-close/detect-historic",
        {
          dry_run,
          tolerance_pct,
          tolerance_min,
        },
      );
      const response: IHistoricDetectionResponse = res.data.data;
      dispatch(detectHistoricSuccess());

      if (onCallback) {
        onCallback(true, response);
      }
    } catch (err: any) {
      dispatch(detectHistoricFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
      if (onCallback) {
        onCallback(false);
      }
    }
  };

// ============================================================================
// Backfill runbook (POST)
// ============================================================================
export const RUN_BACKFILL_RUNBOOK_PROGRESS = "RUN_BACKFILL_RUNBOOK_PROGRESS";
export const RUN_BACKFILL_RUNBOOK_SUCCESS = "RUN_BACKFILL_RUNBOOK_SUCCESS";
export const RUN_BACKFILL_RUNBOOK_FAILED = "RUN_BACKFILL_RUNBOOK_FAILED";

export const runBackfillRunbookProgress = () =>
  action(RUN_BACKFILL_RUNBOOK_PROGRESS);
export const runBackfillRunbookSuccess = () =>
  action(RUN_BACKFILL_RUNBOOK_SUCCESS);
export const runBackfillRunbookFailed = () =>
  action(RUN_BACKFILL_RUNBOOK_FAILED);

interface IRunBackfillRunbookParams {
  skip_historic_detection?: boolean;
  stop_on_calendar_error?: boolean;
}

export const runBackfillRunbookAsync =
  (
    {
      skip_historic_detection,
      stop_on_calendar_error,
    }: IRunBackfillRunbookParams,
    onCallback?: (
      isSuccess: boolean,
      response?: IBackfillRunbookResponse,
    ) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(runBackfillRunbookProgress());

      const res = await api.post(
        "/accounting/gl-monthly-summary/backfill-runbook",
        {
          skip_historic_detection,
          stop_on_calendar_error,
        },
      );
      const response: IBackfillRunbookResponse = res.data.data;
      dispatch(runBackfillRunbookSuccess());

      if (onCallback) {
        onCallback(true, response);
      }
    } catch (err: any) {
      dispatch(runBackfillRunbookFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
      if (onCallback) {
        onCallback(false);
      }
    }
  };

// ============================================================================
// Clear
// ============================================================================
export const CLEAR_GL_SUMMARY_RECONCILE_STATE =
  "CLEAR_GL_SUMMARY_RECONCILE_STATE";
export const clearGLSummaryReconcileState = () =>
  action(CLEAR_GL_SUMMARY_RECONCILE_STATE);
